import clsx from "clsx";
import dayjs from "dayjs";
import { FC, useMemo } from "react";
import {
  useBookingDispatch,
  useBookingState,
} from "../../hooks/useBookingState";
import { ProdAttrUtils } from "../../utils/productAttributeUtils";
import CalendarCustom from "../calendar/CalendarCustom";
import { TimeSlotSelection } from "../timeSlotSelection/TimeSlotSelection";
import { useMusicalData } from "./ProdAttrOfMUSICAL";
import { TimeSlotUtils } from "../../utils/TimeSlotUtils";
import { PA_NAME } from "../../types/product-attribute/product-attribute";
import { useGet3rdProductAvailability } from "../../api/queries/productQueries";

export const ProdAttrCalendar: FC<
  {
    booka?: BookingAttributeDTO;
    pickTimeSlot: boolean;
  } & ProdAttrPickerModeProps
> = ({ booka, pickTimeSlot, ...props }) => {
  const bookingDispatch = useBookingDispatch(props);
  const destDate = booka?.value?.destDate;
  const destTimeslot = booka?.value?.destTimeslot;

  const bookingState = useBookingState(props);
  const bookingAttributes = bookingState.bookingAttributes;
  const defaultBooka = ProdAttrUtils.findDefaultBooka(bookingAttributes);

  const isMusical = defaultBooka?.productAttribute?.name === "MUSICAL";
  const { paId, broadwayAvailability } = useMusicalData({
    enabled: isMusical,
    bookingAttributes,
  });

  const countPerson = ProdAttrUtils.countPerson(bookingState);
  const { data } = useGet3rdProductAvailability({
    productId: bookingState?.product?.id || 0,
    quantity: countPerson || 1,
    date: dayjs(destDate).format("YYYY-MM-DD"),
  });

  const calendarConfig: TCalendarConfig | undefined = (function () {
    if (isMusical) {
      return {
        operatingDays: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        timeSlots: Array.from(
          new Set(
            broadwayAvailability?.map((x) =>
              TimeSlotUtils.broadwayProductTime2Timeslot(x.ProductTime),
            ),
          ),
        ).sort(),
        closedDates: [],
      };
    }
    return ProdAttrUtils.CALENDAR.findCalendarConfig(bookingState,data);
  })();
  const thirdPartyBals = useMemo(
    () =>
      isMusical
        ? bookingAttributes.filter((x) =>
            PA_NAME.PARTY_3RD.includes(
              x.productAttribute.name as ProdAttrName3rdParty,
            ),
          )
        : [],
    [bookingAttributes, isMusical],
  );
  return (
    <div
      className={clsx(
        "mb-5 flex flex-col gap-6 sm:flex-row",
        props.mode === "POPUP" && "sm:flex-wrap",
      )}
    >
      <div className="min-w-full sm:min-w-[19rem]">
        <CalendarCustom
          calendarConfig={calendarConfig}
          disablePastDates={true}
          defaultDate={destDate ? dayjs(destDate).toDate() : undefined}
          onDayClick={(date: Date) => {
            const destDate = dayjs(date).format("YYYYMMDD");
            booka &&
              bookingDispatch.bookingPickLs([
                {
                  ...booka,
                  value: {
                    ...booka.value,
                    pickLater:
                      !booka.value?.pickLater ||
                      ((!pickTimeSlot || booka?.value?.destTimeslot) &&
                        destDate)
                        ? undefined
                        : booka.value?.pickLater,
                    destDate,
                  },
                },
                ...(isMusical
                  ? (thirdPartyBals ?? []).map((x) => {
                      return {
                        ...x,
                        value: undefined,
                      };
                    })
                  : []),
              ]);
          }}
        />
      </div>

      {pickTimeSlot && (
        <TimeSlotSelection
          mode={props.mode}
          destDate={destDate}
          productId={props?.d?.id}
          calendarConfig={calendarConfig}
          activeTimeSlots={[destTimeslot].filter((x) => x)}
          onTimeSlotChange={(timeSlotValue: string) =>
            booka &&
            bookingDispatch.bookingPick({
              ...booka,
              value: {
                ...booka?.value,
                pickLater:
                  !booka.value?.pickLater ||
                  (timeSlotValue && booka?.value?.destDate)
                    ? undefined
                    : booka.value?.pickLater,
                destTimeslot: timeSlotValue, // eg: 0800, 1900
              },
            })
          }
        />
      )}
    </div>
  );
};
